export class CreditCard {
    constructor(data) {
        this.id = data.id;
        this.last4 = data.lastFourDigit;
        this.expMonth = data.expireMonth;
        this.expYear = data.expireYear;
    }

    getId() {
        return this.id;
    }

    getLast4() {
        return this.last4;
    }

    getExp() {
        return (this.expMonth < 10 ? "0" : "") + this.expMonth + "/" + this.expYear;
    }

    getDisplayText() {
        return `Card ending in ${this.getLast4()}`;
    }

    getBillingKeyType() {
        return "CARD";
    }

    getBillingKey() {
        return String(this.getId());
    }

    getOptionValue() {
        return `${this.getBillingKeyType()}:${this.getBillingKey()}`;
    }

    toBillingSourceOption() {
        return {
            value: this.getOptionValue(),
            label: this.getDisplayText(),
            type: this.getBillingKeyType(),
            key: this.getBillingKey(),
        };
    }
}

export class CreditCardInfo {
    constructor(data) {
        this.stripeEmail = data.stripeEmail;
        this.adminId = data.adminId;
        this.cards = (data.cardInfos ?? []).map(card => new CreditCard(card));   
    }


    getStripeEmail() {
        return this.stripeEmail;
    }

    getAdminId() {
        return this.adminId;
    }

    getCards() {
        return this.cards;
    }
}

export class PaymentSource {
    constructor(data) {
        this.companyId = data.companyId;
        this.billingSource = data.billingSource;
        this.administratorId = data.administratorId;
        this.billingKeyType = data.billingKeyType;
        this.billingKey = data.billingKey;
    }

    getCompanyId() {
        return this.companyId;
    }

    getBillingSource() {
        return this.billingSource;
    }

    getAdministratorId() {
        return this.administratorId;
    }

    getBillingKeyType() {
        return this.billingKeyType;
    }

    getBillingKey() {
        return this.billingKey;
    }
}

export class PaymentSources {
    constructor(data) {
        this.sources = data.map(source => new PaymentSource(source));
    }
    
    getSources() {
        return this.sources;
    }

    getCompaniesWithCard(cardId) {
        return this.sources.filter(source => 
            source.getBillingSource() === "STRIPE" &&
            source.getBillingKeyType() === "CARD" &&
            source.getBillingKey() === String(cardId)
        )
    }

    getCompaniesWithUsBankAccount(achId) {
        return this.sources.filter(source => 
            source.getBillingSource() === "STRIPE" &&
            source.getBillingKeyType() === "US_BANK_ACCOUNT" &&
            source.getBillingKey() === String(achId)
        )
    }
}

export class UsBankAccount {
    constructor(data) {
        this.id = data.id;
        this.last4 = data.last4;
        this.bankName = data.bankName;
        this.routingNumber = data.routingNumber;
        this.achEnabled = data.achEnabled;
    }

    getId() {
        return this.id;
    }

    getLast4() {
        return this.last4 || "****";
    }

    getBankName() {
        return this.bankName || "Bank";
    }

    getRoutingNumber() {
        return this.routingNumber || "****";
    }

    isEnabled() {
        return this.achEnabled !== false;
    }

    getDisplayText() {
        return `Bank account ending in ${this.getLast4()} (${this.getBankName()})`;
    }

    getBillingKeyType() {
        return "US_BANK_ACCOUNT";
    }

    getBillingKey() {
        return String(this.getId());
    }

    getOptionValue() {
        return `${this.getBillingKeyType()}:${this.getBillingKey()}`;
    }

    toBillingSourceOption() {
        return {
            value: this.getOptionValue(),
            label: this.getDisplayText(),
            type: this.getBillingKeyType(),
            key: this.getBillingKey(),
        };
    }
}

export class UsBankAccountInfo {
    constructor(data) {
        this.stripeEmail = data?.stripeEmail;
        this.adminId = data?.adminId;
        this.banks = (data?.achInfos ?? []).map((bank) => new UsBankAccount(bank));
    }

    getStripeEmail() {
        return this.stripeEmail;
    }

    getAdminId() {
        return this.adminId;
    }

    getBanks() {
        return this.banks;
    }
}

class CardCharge {
    constructor(data) {
        this.chargeId = data.chargeId;
        this.paidAmount = data.totalAmountPaid;
        this.currency = data.currency;
        this.success = data.isSuccessful;
        this.chargeTime = data.chargeTime;
        this.invoiceUrl = data.invoiceUrl;
        this.paymentMethodType = data.paymentMethodType;
        this.cardLastFourDigit = data.cardLastFourDigit;
        this.achLastFourDigit = data.achLastFourDigit;
        this.achBankName = data.achBankName;
    }

    getChargeId() {
        return this.chargeId;
    }

    getPaidAmountInUsd() {
        return this.paidAmount / 100;
    }

    getCurrency() {
        return this.currency;
    }

    getSuccess() {
        return this.success;
    }

    getChargeTime() {
        return this.chargeTime;
    }

    getInvoiceUrl() {
        return this.invoiceUrl;
    }

    getPaymentMethodType() {
        return this.paymentMethodType;
    }

    getCardLastFourDigit() {
        return this.cardLastFourDigit;
    }

    getAchLastFourDigit() {
        return this.achLastFourDigit;
    }

    getAchBankName() {
        return this.achBankName;
    }

    getSourceDisplayText() {
        if (this.paymentMethodType === "US_BANK_ACCOUNT" && this.achLastFourDigit) {
            return `Bank Account ending in ${this.achLastFourDigit}`;
        } else if (this.paymentMethodType === "CARD" && this.cardLastFourDigit) {
            return `Card ending in ${this.cardLastFourDigit}`;
        }
        return "";
    }
}

export class CardChargeHistory {
    constructor(data) {
        this.charges = data.map(charge => new CardCharge(charge));
    }

    getCharges() {
        return this.charges;
    }
}

export class PlansList {
    static _flavourConfigs = {
        BRANDED_ELITE: {
            name: "Branded Elite",
            paid: true,
        },
        BRANDED_LEGACY: {
            name: "Branded Premium",
            paid: true,
        },
        BRANDED_PREMIUM: {
            name: "Branded Premium",
            paid: true,
        },
        BUSINESS_ADVANCED: {
            name: "Business Advanced",
            paid: true,
        },
        PROFESSIONAL: {
            name: "Professional",
            paid: true,
        },
        STARTER: {
            name: "Starter",
            paid: false,
        },
        TRIAL: {
            name: "Trial (Professional)",
            paid: false,
        },
    }

    constructor(data) {
        this.flavourMapping = data.reduce((mapping, plan) => {
            mapping[plan.planName] = plan.flavour;
            return mapping;
        }, {});
        this.displayMapping = data.reduce((mapping, plan) => {
            mapping[plan.planName] = plan.displayName;
            return mapping;
        }, {});
    }

    isPaid(planName) {
        const flavour = this.flavourMapping[planName];
        return PlansList._flavourConfigs?.[flavour]?.paid ?? false;
    }

    getDisplayName(planName) {
        return this.displayMapping?.[planName] ?? planName;
    }

    canOfferReviewResponse(planName) {
        const flavour = this.flavourMapping?.[planName] ?? "";
        return flavour === "BRANDED_ELITE" || flavour === "BUSINESS_ADVANCED";
    }

    getAllPlanNames() {
        return Array.from(new Set(Object.values(this.displayMapping)));
    }
}
